import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Truck, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { ThemeToggle } from "@/context/ThemeContext";
import { Field } from "@/components/common";
import { errMsg } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1616432043562-3671ea2e5242?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to={!user.platform_role && (user.companies || []).length > 1 && !localStorage.getItem("activeCompany") ? "/select-company" : "/"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const data = await login(email, password);
      navigate(!data.platform_role && (data.companies || []).length > 1 ? "/select-company" : "/");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden overflow-hidden lg:block">
        <img src={HERO} alt="Truck fleet on highway" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-slate-950/65" />
        <div className="relative flex h-full flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2.5">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-500"><Truck className="h-5 w-5" /></div>
            <span className="font-display text-2xl font-extrabold tracking-tight">TransLogiX</span>
          </div>
          <div className="max-w-md">
            <h1 className="text-4xl sm:text-5xl font-extrabold leading-[1.05] tracking-tight">Every lorry, every LR, every rupee — on one board.</h1>
            <p className="mt-4 text-base text-slate-300">Multi-company fleet, bilty, trip expense and ledger management for Indian transporters.</p>
          </div>
          <div className="flex gap-8 text-sm text-slate-300">
            <span><b className="block font-mono text-xl text-white">LR → POD</b>end-to-end trips</span>
            <span><b className="block font-mono text-xl text-white">GST</b>ready invoices</span>
          </div>
        </div>
      </div>
      <div className="relative flex items-center justify-center p-6 sm:p-12">
        <ThemeToggle className="absolute right-6 top-6" />
        <form onSubmit={submit} className="w-full max-w-sm space-y-5 fade-up" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-2 mb-6">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground"><Truck className="h-5 w-5" /></div>
            <span className="font-display text-xl font-extrabold">TransLogiX</span>
          </div>
          <div>
            <h2 className="text-3xl font-bold tracking-tight">Sign in</h2>
            <p className="mt-1 text-sm text-muted-foreground">Accounts are created by your company admin.</p>
          </div>
          <Field label="Email"><Input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" /></Field>
          <Field label="Password"><Input data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-11" /></Field>
          {error && <div data-testid="login-error" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300">{error}</div>}
          <Button data-testid="login-submit-btn" disabled={busy} className="h-11 w-full gap-2 text-sm font-semibold">
            {busy ? "Signing in…" : "Sign in"} <ArrowRight className="h-4 w-4" />
          </Button>
        </form>
      </div>
    </div>
  );
}
